import React from "react";
import { useQuery } from "react-query";
import {
  getTemplateChecklistById,
  getTemplateChecklistsByPdcId,
  getTemplateChecklistItemsByChecklistId,
  getAllTemplateChecklists
} from "services/api/templateChecklist/templateChecklistApi";

const api = {
  fetchTemplateChecklistById: async (id: number) => {
    const { data } = await getTemplateChecklistById(id);
    return data;
  },

  fetchTemplateChecklistsByPdcId: async (pdcId: number) => {
    const { data } = await getTemplateChecklistsByPdcId(pdcId);
    return data;
  },

  fetchAllTemplateChecklists: async () => {
    const { data } = await getAllTemplateChecklists();
    return data;
  },

  fetchTemplateChecklistItemsByChecklistId: async (checklistId: number) => {
    const { data } = await getTemplateChecklistItemsByChecklistId(checklistId);
    return data;
  },
}; 

export const useFetchTemplateChecklistById = (id: number) => {
  const { isLoading: templateChecklistLoading, data: templateChecklistData } = useQuery(
    ["templateChecklist", id],
    () => api.fetchTemplateChecklistById(id),
    {
      enabled: !!id,
      staleTime: Infinity,
    }
  );

  return {
    templateChecklistLoading,
    templateChecklistData,
  };
};

export const useFetchTemplateChecklistsByPdcId = (pdcId: number) => {
  const { isLoading: templateChecklistsLoading, data: templateChecklistsData } = useQuery(
    ["templateChecklistsByPdc", pdcId],
    () => api.fetchTemplateChecklistsByPdcId(pdcId),
    {
      enabled: !!pdcId,
      staleTime: Infinity,
    }
  );

  return {
    templateChecklistsLoading,
    templateChecklistsData,
  };
};

// 🚀 HOOK EFICIENTE: Uma única chamada para todos os templates
export const useFetchAllTemplateChecklists = () => {
  const { isLoading: allTemplatesLoading, data: allTemplatesData } = useQuery(
    ["allTemplateChecklists"],
    () => api.fetchAllTemplateChecklists(),
    {
      staleTime: Infinity,
      retry: 1
    }
  );

  // Organizar dados por PDC para acesso eficiente
  const templatesByPdc = React.useMemo(() => {
    if (!allTemplatesData) return {};
    
    return allTemplatesData.reduce((acc, template) => {
      if (!acc[template.pdcId]) {
        acc[template.pdcId] = [];
      }
      acc[template.pdcId].push(template);
      return acc;
    }, {});
  }, [allTemplatesData]);

  // Função para verificar status de uma PDC específica
  const getTemplateStatusForPdc = (pdcId: number) => {
    const templates = templatesByPdc[pdcId] || [];
    return {
      exists: templates.length > 0,
      templates,
      count: templates.length
    };
  };

  return {
    allTemplatesLoading,
    allTemplatesData,
    templatesByPdc,
    getTemplateStatusForPdc
  };
};

export const useFetchTemplateChecklistItemsByChecklistId = (checklistId: number) => {
  const { isLoading: templateChecklistItemsLoading, data: templateChecklistItemsData } = useQuery(
    ["templateChecklistItems", checklistId],
    () => api.fetchTemplateChecklistItemsByChecklistId(checklistId),
    {
      enabled: !!checklistId,
      staleTime: Infinity,
    }
  );

  return {
    templateChecklistItemsLoading,
    templateChecklistItemsData,
  };
};